import type { AssetId } from '../assets/base.ts';
import type { CharacterModifierId, CharacterModifierType, PermissionGroup, PermissionTypeInvalid } from '../gameLogic/index.ts';
import type { SpaceRole, SpaceRoleOrNone } from '../space/spaceRoles.ts';
import type { AssetPreferenceResolution } from './assetPreferences.ts';
import type { CharacterId } from './characterTypes.ts';

export enum ItemInteractionType {
	/**
	 * Special interaction that doesn't have prerequisites from the character itself.
	 */
	ACCESS_ONLY = 'ACCESS_ONLY',
	/**
	 * Special interaction for changing expression
	 */
	EXPRESSION_CHANGE = 'EXPRESSION_CHANGE',
	/**
	 * Item modified only in stylistic way (e.g. color)
	 */
	STYLING = 'STYLING',
	/**
	 * Item being modified (e.g. changing its behavior or configuration)
	 */
	MODIFY = 'MODIFY',
	/**
	 * Item modified by changing its name, description, or other create-time properties
	 */
	CUSTOMIZE = 'CUSTOMIZE',
	/**
	 * Item being added or removed.
	 */
	ADD_REMOVE = 'ADD_REMOVE',
	/**
	 * Item being reordered.
	 */
	REORDER = 'REORDER',
	/**
	 * Character is entering or leaving a room device.
	 * This action happens on _both_ the device itself and the wearable part.
	 */
	DEVICE_ENTER_LEAVE = 'DEVICE_ENTER_LEAVE',
}

export type PermissionRestriction = {
	type: 'missingPermission';
	target: CharacterId;
	permissionGroup: PermissionGroup;
	permissionId: string;
	permissionDescription: string;
	permissionResult: PermissionTypeInvalid;
};

export type Restriction =
	| PermissionRestriction
	| {
		type: 'missingAssetPermission';
		target: CharacterId;
		resolution: AssetPreferenceResolution;
	}
	| {
		type: 'blockedAddRemove';
		asset: AssetId;
		itemName: string;
	}
	| {
		type: 'blockedModule';
		asset: AssetId;
		itemName: string;
		module: string;
	}
	| {
		type: 'covered';
		asset: AssetId;
		itemName: string;
		attribute: string;
	}
	| {
		type: 'blockedHands';
	}
	| {
		type: 'blockedMove';
	}
	| {
		type: 'safemodeInteractOther';
	}
	| {
		type: 'modifyBodyRoom';
	}
	| {
		type: 'missingSpaceRole';
		actual: SpaceRole;
		required: SpaceRoleOrNone;
	}
	| {
		type: 'itemCustomizeOther';
	}
	| {
		type: 'inRoomDevice';
	}
	| {
		type: 'tooFar';
		subtype: 'characterInteraction' | 'roomTarget' | 'followTarget';
	}
	| {
		type: 'blockedByCharacterModifier';
		modifierId: CharacterModifierId;
		modifierType: CharacterModifierType;
	}
	| {
		type: 'characterModifierLocked'; // The modifier is protected by a locked lock
		modifierId: CharacterModifierId;
		modifierType: CharacterModifierType;
		modifierName: string;
	}
	// Generic catch-all problem, supposed to be used when something simply went wrong (like bad data, target not found, and so on...)
	| {
		type: 'invalid';
	};
